import {
  type ComponentPropsWithoutRef,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useId,
  useState,
} from "react";
import { ChevronDown } from "lucide-react";
import { IconButton } from "./button";
import { MicroLabel } from "./MicroLabel";
import { cn } from "@/lib/utils";

/**
 * A panel that folds, and the state and wiring of the control that folds it.
 *
 * The panel element stays in the tree and goes `hidden`, so the control can
 * always name what it controls. Whether its *contents* mount while closed is
 * the caller's: a list of tag definitions costs nothing to keep, and a row's
 * body of citations and draft fields costs enough to be worth mounting on the
 * way open.
 *
 * What opens it is the caller's too. A one-word header is a good control and a
 * good accessible name; a row whose header is a title and a shelf of pills
 * labels a chevron beside it instead.
 */
export function useDisclosure(
  initiallyOpen = false,
  /** Whether it is open, held by the caller when something else reads it, as a page's "Collapse all" reads its cards. */
  held?: { open: boolean; setOpen: (open: boolean) => void },
) {
  const panelId = useId();
  const [own, setOwn] = useState(initiallyOpen);
  const open = held?.open ?? own;
  const setOpen: Dispatch<SetStateAction<boolean>> =
    held === undefined
      ? setOwn
      : (next) => {
          held.setOpen(typeof next === "function" ? next(held.open) : next);
        };
  return {
    open,
    setOpen,
    /** Spread on whatever opens it. Give it a label unless its content is one. */
    trigger: {
      type: "button" as const,
      "aria-expanded": open,
      "aria-controls": panelId,
      onClick: () => {
        setOpen(!open);
      },
    },
    /** Spread on the panel. Mount its contents behind `open` when they are heavy. */
    panel: { id: panelId, hidden: !open },
  };
}

/**
 * Open every fold an element sits inside, outermost first, so something that
 * jumps to it lands on it rather than on a panel that is hidden. Answers
 * whether anything was opened, so a caller can wait a frame for it to draw.
 *
 * It presses each fold's own control, the one naming the panel it controls,
 * so a fold opened this way is in the state its owner holds, not forced open
 * underneath it.
 */
export function openFoldsAround(element: HTMLElement): boolean {
  const controls: HTMLElement[] = [];
  for (let at = element.parentElement; at !== null; at = at.parentElement) {
    if (!at.hidden || at.id === "") continue;
    const control = document.querySelector<HTMLElement>(
      `[aria-controls="${CSS.escape(at.id)}"][aria-expanded="false"]`,
    );
    if (control !== null) controls.unshift(control);
  }
  for (const control of controls) control.click();
  return controls.length > 0;
}

/**
 * The mark that says a panel folds: pointing right while it is shut, down once
 * it is open, as the design draws every fold.
 *
 * One direction. The same chevron used to point right on one panel and down on
 * another for the same state, so it said nothing a reader could rely on.
 */
export function DisclosureChevron({
  open,
  className,
}: {
  open: boolean;
  className?: string;
}) {
  return (
    <ChevronDown
      aria-hidden
      className={cn(
        "h-4 w-4 shrink-0 transition-transform",
        !open && "-rotate-90",
        className,
      )}
    />
  );
}

/**
 * The chevron and the control around it, for a row whose header is already a
 * title and a shelf of pills and so cannot be the control itself.
 *
 * Two rows drew this: a position under the analysis and the same position under
 * the list, each with its own spelling of the label and one of them resetting
 * an edit on the way closed. `onToggle` is what a caller does *besides* folding
 * the panel, not instead of it.
 */
export function DisclosureToggle({
  trigger,
  open,
  names,
  onToggle,
}: {
  /** Loose rather than the hook's own shape: one row takes its trigger as a prop. */
  trigger: ComponentPropsWithoutRef<"button">;
  open: boolean;
  /** What folds, named for a reader who cannot see the row. */
  names: string;
  onToggle?: () => void;
}) {
  return (
    <IconButton
      {...trigger}
      aria-label={`${open ? "Collapse" : "Expand"} ${names}`}
      onClick={(event) => {
        onToggle?.();
        trigger.onClick?.(event);
      }}
      variant="quiet"
      size="dense"
    >
      <DisclosureChevron open={open} />
    </IconButton>
  );
}

/**
 * A heading row that folds what is under it: the title and what it holds are
 * the control, with a leading slot for a handle or an icon and a trailing slot
 * for controls of the row's own.
 *
 * The design makes the whole row the place to press. A row holding a drag handle
 * and its own buttons cannot be one button, since a control inside a control is
 * nothing a reader can operate, so the stretch between them is: the chevron,
 * the title and the counts beside it.
 */
export function DisclosureHeader({
  trigger,
  open,
  title,
  meta,
  lead,
  className,
}: {
  trigger: ComponentPropsWithoutRef<"button">;
  open: boolean;
  title: ReactNode;
  /** What the panel holds, in a few words: read as the control's description. */
  meta?: ReactNode;
  lead?: ReactNode;
  className?: string;
}) {
  const titleId = useId();
  const metaId = useId();
  return (
    <div className={cn("flex items-center gap-[11px]", className)}>
      {lead}
      <button
        {...trigger}
        aria-labelledby={titleId}
        aria-describedby={meta === undefined ? undefined : metaId}
        className="flex min-w-0 flex-1 cursor-pointer flex-wrap items-center gap-x-[11px] gap-y-1 text-left select-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <DisclosureChevron
          open={open}
          className="size-[17px] text-subtle-foreground"
        />
        <span id={titleId} className="min-w-0 flex-none">
          {title}
        </span>
        {meta !== undefined && (
          <span id={metaId} className="ml-auto flex items-center gap-2.5">
            {meta}
          </span>
        )}
      </button>
    </div>
  );
}

/**
 * A short fold drawn as v0.28's boxed bar: a caption in small capitals with
 * the chevron before it, and what it folds under it in the box, as a question
 * says why it is asked.
 */
export function DisclosureBar({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const fold = useDisclosure(false);
  return (
    <div className="rounded-control border border-border bg-background text-[11.5px] leading-[1.5] text-muted-foreground">
      <button
        {...fold.trigger}
        className="group flex w-full cursor-pointer items-center gap-1.5 rounded-control px-2.5 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <DisclosureChevron open={fold.open} className="size-3" />
        <MicroLabel
          size="sm"
          className="text-[10px] text-muted-foreground group-hover:text-foreground"
        >
          {label}
        </MicroLabel>
      </button>
      <div {...fold.panel} className="space-y-1 pr-[11px] pb-2 pl-7">
        {children}
      </div>
    </div>
  );
}
